import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuthStore } from '../../../store/authStore';
import toast from 'react-hot-toast';
import { Upload, FileText, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { API_BASE_URL } from '../../../services/api';

const API_URL = process.env.REACT_APP_API_URL || `${API_BASE_URL}/api`;

const CreateCampaign = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  
  // Form State
  const [name, setName] = useState('');
  const [fileData, setFileData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Mapping State
  const [phoneColumn, setPhoneColumn] = useState('');
  const [countryCodeColumn, setCountryCodeColumn] = useState('');
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [variableMap, setVariableMap] = useState({}); // { "1": "ExcelColName", "2": "ExcelColName" }

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (step === 2) {
      fetchTemplates();
    }
  }, [step]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.data);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
      
      if (data.length === 0) {
        toast.error('Excel file is empty');
        return;
      }
      
      const cols = Object.keys(data[0]);
      setColumns(cols);
      setFileData(data);
      setStep(2);
    };
    reader.readAsBinaryString(file);
  };

  const getTemplateVariables = (templateBody) => {
    const regex = /\{\{(\d+)\}\}/g;
    let match;
    const vars = new Set();
    while ((match = regex.exec(templateBody)) !== null) {
      vars.add(match[1]);
    }
    return Array.from(vars).sort((a, b) => parseInt(a) - parseInt(b));
  };

  const handleCreate = async () => {
    if (!name || !phoneColumn || !selectedTemplate) {
      return toast.error('Please fill in all required fields');
    }
    
    // Validate mapping
    const templateVars = getTemplateVariables(selectedTemplate.components.find(c => c.type === 'BODY')?.text || '');
    for (const v of templateVars) {
      if (!variableMap[v]) return toast.error(`Please map column for variable {{${v}}}`);
    }

    try {
      setLoading(true);
      
      const recipients = fileData.map(row => {
        const rowVars = {};
        for (const [vId, colName] of Object.entries(variableMap)) {
          rowVars[vId] = String(row[colName] || '');
        }
        const cCode = countryCodeColumn ? String(row[countryCodeColumn] || '') : '';
        const pNum = String(row[phoneColumn] || '');
        return {
          phone: cCode ? `${cCode}${pNum}` : pNum,
          variables: rowVars
        };
      });

      const headerComp = selectedTemplate?.components?.find(c => c.type === 'HEADER');
      const headerType = headerComp?.format ? headerComp.format.toLowerCase() : undefined;

      await axios.post(`${API_URL}/whatsapp/campaigns`, {
        name,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        headerImageUrl: headerImageUrl || undefined,
        headerType,
        recipients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Campaign launched successfully!');
      navigate('/whatsapp-campaigns');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to launch campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Create Campaign</h1>
      
      {/* Stepper */}
      <div className="flex items-center mb-8">
        <div className={`flex flex-col items-center flex-1 ${step >= 1 ? 'text-blue-600' : 'text-slate-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${step >= 1 ? 'border-blue-600 bg-blue-50' : 'border-slate-300'}`}>1</div>
          <span className="text-sm mt-2 font-medium">Upload Data</span>
        </div>
        <div className="flex-1 h-0.5 bg-slate-200"><div className={`h-full bg-blue-600 transition-all ${step >= 2 ? 'w-full' : 'w-0'}`}></div></div>
        <div className={`flex flex-col items-center flex-1 ${step >= 2 ? 'text-blue-600' : 'text-slate-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${step >= 2 ? 'border-blue-600 bg-blue-50' : 'border-slate-300'}`}>2</div>
          <span className="text-sm mt-2 font-medium">Select Template</span>
        </div>
        <div className="flex-1 h-0.5 bg-slate-200"><div className={`h-full bg-blue-600 transition-all ${step >= 3 ? 'w-full' : 'w-0'}`}></div></div>
        <div className={`flex flex-col items-center flex-1 ${step >= 3 ? 'text-blue-600' : 'text-slate-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${step >= 3 ? 'border-blue-600 bg-blue-50' : 'border-slate-300'}`}>3</div>
          <span className="text-sm mt-2 font-medium">Map & Launch</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Campaign Name</label>
              <input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500" 
                placeholder="E.g., Diwali Offer 2026"
              />
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 flex flex-col items-center justify-center text-center hover:bg-slate-50 transition-colors relative">
              <input 
                type="file" 
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" 
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Upload size={40} className="text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-1">Upload your Excel or CSV file</h3>
              <p className="text-sm text-slate-500">Must contain phone numbers (with an optional separate country code column) and any dynamic variables.</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {loading ? <p className="text-center py-8 text-slate-500">Loading templates...</p> : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map(tmpl => (
                  <div 
                    key={tmpl.id} 
                    onClick={() => setSelectedTemplate(tmpl)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedTemplate?.id === tmpl.id ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-slate-800">{tmpl.name}</h4>
                      <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded capitalize">{tmpl.category}</span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-3">
                      {tmpl.components.find(c => c.type === 'BODY')?.text || 'No body content'}
                    </p>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex justify-between mt-8">
              <div className="flex items-center gap-3">
                <button onClick={() => setStep(1)} className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-100 rounded-lg flex items-center gap-2">
                  <ArrowLeft size={16} /> Back
                </button>
                {/* <a 
                  href="https://business.facebook.com/wa/manage/message-templates/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-blue-600 font-medium hover:bg-blue-50 rounded-lg flex items-center gap-2 border border-blue-200 transition-colors"
                >
                  Manage Templates in Meta
                </a> */}
              </div>
              <button 
                onClick={() => {
                  if(!selectedTemplate) return toast.error('Select a template');
                  setStep(3);
                }} 
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">Template Preview</h3>
              <p className="text-slate-600 whitespace-pre-wrap text-sm">
                {selectedTemplate?.components.find(c => c.type === 'BODY')?.text}
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800">Map Columns to Data</h3>
              
              <div className="grid grid-cols-2 gap-4 items-center">
                <span className="text-sm font-medium text-slate-700">Country Code Column (Optional)</span>
                <select 
                  className="border border-slate-300 rounded-lg p-2 outline-none"
                  value={countryCodeColumn}
                  onChange={(e) => setCountryCodeColumn(e.target.value)}
                >
                  <option value="">None (Already in Phone column)</option>
                  {columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4 items-center">
                <span className="text-sm font-medium text-slate-700">Phone Number Column <span className="text-red-500">*</span></span>
                <select 
                  className="border border-slate-300 rounded-lg p-2 outline-none"
                  value={phoneColumn}
                  onChange={(e) => setPhoneColumn(e.target.value)}
                >
                  <option value="">Select column...</option>
                  {columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {getTemplateVariables(selectedTemplate?.components.find(c => c.type === 'BODY')?.text || '').map(v => (
                <div key={v} className="grid grid-cols-2 gap-4 items-center">
                  <span className="text-sm font-medium text-slate-700">Variable {`{{${v}}}`} <span className="text-red-500">*</span></span>
                  <select 
                    className="border border-slate-300 rounded-lg p-2 outline-none"
                    value={variableMap[v] || ''}
                    onChange={(e) => setVariableMap(prev => ({...prev, [v]: e.target.value}))}
                  >
                    <option value="">Select column...</option>
                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}

              {selectedTemplate?.components?.find(c => c.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(c.format)) && (
                <div className="grid grid-cols-2 gap-4 items-center mt-4 pt-4 border-t border-slate-200">
                  <span className="text-sm font-medium text-slate-700">Header Media URL (Image/Video/Doc) <span className="text-red-500">*</span></span>
                  <input 
                    type="url"
                    placeholder="https://example.com/media.mp4"
                    className="border border-slate-300 rounded-lg p-2 outline-none"
                    value={headerImageUrl}
                    onChange={(e) => setHeaderImageUrl(e.target.value)}
                    required
                  />
                </div>
              )}
            </div>

            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(2)} className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-100 rounded-lg flex items-center gap-2">
                <ArrowLeft size={16} /> Back
              </button>
              <button 
                onClick={handleCreate} 
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Launching...' : 'Launch Campaign'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateCampaign;
